import { createUuid } from '@notifi/uuid/browser';
import { normalizeMembershipCreditAmount } from '@/components/organisms/retention/membership-save-offer';
import type { PlanRecurringServiceInput } from '@/hooks/membership-plans/usePlanRecurringServices';
import {
  extraUnitPriceOf,
  type MembershipExtraUnitPrice,
  type MembershipExtraUnitPriceUnit,
} from '@/lib/membership/extra-unit-price';

// Shared types, constants, and helpers for membership plan components

export type DiscountType = 'PERCENT' | 'FLAT' | 'SET_PRICE';
export type PlanStatus = 'ACTIVE' | 'ARCHIVED';
export type BillingIntervalUnit = 'MONTHS' | 'YEARS';
export type BillingFrequency = 'MONTHLY' | 'QUARTERLY' | 'SEMI_ANNUAL' | 'ANNUAL';
export type MembershipBillingOptionFrequency =
  | 'MONTHLY'
  | 'BIMONTHLY'
  | 'QUARTERLY'
  | 'SEMIANNUAL'
  | 'ANNUAL'
  | 'UPFRONT';
/** Fixed contract terms a billing option can promise; 0 means ongoing. */
export const MEMBERSHIP_BILLING_FIXED_TERMS = [12, 24, 36, 48, 60] as const;
export type MembershipBillingTermMonths = 0 | (typeof MEMBERSHIP_BILLING_FIXED_TERMS)[number];

export function isMembershipBillingFixedTerm(
  months: number,
): months is (typeof MEMBERSHIP_BILLING_FIXED_TERMS)[number] {
  return (MEMBERSHIP_BILLING_FIXED_TERMS as readonly number[]).includes(months);
}

export interface MembershipBillingOption {
  id: string;
  label: string;
  termMonths: MembershipBillingTermMonths;
  frequency: MembershipBillingOptionFrequency;
  basePrice: number;
  isDefault: boolean;
  /**
   * Free, office-only way into the plan: a $0 base over a fixed term. Extra
   * units and the setup fee still bill; the customer never sees it among their
   * choices, and it can't be the default.
   */
  isComplimentary?: boolean;
}
/**
 * A coverage rule as Operations stores it. Plans saved before yearly add-on
 * pricing carry only `extraUnitPriceMonthly`, so read the price through
 * `extraUnitPriceOf`, never off the fields directly.
 */
export interface MembershipCoverageRule {
  id: string;
  equipmentCategory: string;
  includedQuantity: number;
  /** What one extra unit costs, in `extraUnitPricePer`. */
  extraUnitPrice?: number;
  extraUnitPricePer?: MembershipExtraUnitPriceUnit;
  /** Monthly equivalent Operations keeps beside the price. */
  extraUnitPriceMonthly?: number;
  isPrimary: boolean;
}

/** A coverage rule being authored: the price always in a unit the author chose. */
export type MembershipCoverageRuleDraft = Omit<
  MembershipCoverageRule,
  'extraUnitPrice' | 'extraUnitPricePer' | 'extraUnitPriceMonthly'
> &
  MembershipExtraUnitPrice;
export type CoveredServicePeriod = 'BILLING_CYCLE' | 'ANNUAL' | 'CONTRACT_TERM' | 'LIFETIME';
export type RolloverPolicy = 'EXPIRE' | 'ROLLOVER_LIMITED' | 'ROLLOVER_FULL';
export type NoShowPolicy = 'REDEEM' | 'PRESERVE';
export type ReschedulePolicy = 'PRESERVE' | 'REDEEM_IF_LATE';
export type WaiverType = 'FULL_WAIVE' | 'DISCOUNT_TO_ZERO' | 'FIXED_CREDIT';
export type CancelMode = 'IMMEDIATE' | 'AT_PERIOD_END';
export type EtfType = 'FLAT' | 'PERCENT_REMAINING';
export type SaveOfferType = 'DOWNGRADE' | 'FREE_MONTH' | 'CREDIT' | 'PAUSE' | 'SCHEDULE_SERVICE';

export interface DiscountTier {
  label: string;
  appliesTo: string[];
  specificItemIds?: string[];
  type: DiscountType;
  value: number;
}

export interface CoveredService {
  lineItemId?: string;
  name: string;
  quantityPerPeriod: number;
  period: CoveredServicePeriod;
  rolloverPolicy: RolloverPolicy;
  rolloverMax?: number | null;
  noShowPolicy: NoShowPolicy;
  reschedulePolicy: ReschedulePolicy;
  seasonalWindow?: { startMonth: number; endMonth: number } | null;
  /**
   * NOT-598 — equipment category (EquipmentCategory.key) this service maintains.
   * When set, a booked tune-up links the member's matching unit to the job.
   */
  equipmentCategory?: string | null;
  /** NOT-598 — job type this service books as (else the tenant tune-up default). */
  jobTypeId?: string | null;
  /**
   * First-tune-up offset (months): hold the first offer until the equipment
   * install date (else enrollment) + N months, so a just-installed unit isn't
   * offered a tune-up on day one. Null/0 = offer as soon as otherwise due.
   */
  firstOfferOffsetMonths?: number | null;
  /** NOT-598 — VISIT is auto-scheduled; PERK/DISCOUNT are not. */
  kind?: CoveredServiceKind;
}

export type CoveredServiceKind = 'VISIT' | 'PERK' | 'DISCOUNT';

export interface FeeWaiver {
  lineItemId?: string;
  name: string;
  waiverType: WaiverType;
  /** Legacy billing-cycle allowance. Existing plans preserve this value, but
   * new waivers remain unlimited while the membership is active. */
  maxPerPeriod?: number | null;
  /** Fixed $ credit — only used when waiverType === 'FIXED_CREDIT'. */
  value?: number;
}

export interface SaveOfferTemplate {
  type: SaveOfferType;
  value?: number;
  label: string;
}

export interface MembershipPlan {
  id?: string;
  tenantId: string;
  name: string;
  description: string;
  status: PlanStatus;
  discountPercent: number;
  priceAmount: number;
  priceCurrency: string;
  billingIntervalValue: number;
  billingIntervalUnit: BillingIntervalUnit;
  billingProvider?: string | null;
  providerProductId?: string | null;
  providerPriceId?: string | null;
  providerSyncedAt?: string | null;
  providerSyncError?: string | null;
  eligibleLineItemTypes: string[];
  eligibleLineItemIds: string[];
  excludedLineItemIds: string[];
  priority: number;
  planVersion: number;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  setupFee?: number;
  billingOptions?: MembershipBillingOption[];
  coverageRules?: MembershipCoverageRule[];
  loadedLaborRate?: number;
  costUtilizationPercent?: number;
  revenueRecognitionMode?: 'AT_SALE' | 'STRAIGHT_LINE' | 'PER_VISIT' | 'VISIT_LINKED' | null;
  visitRecognitionPercent?: number | null;
  deferredRevenueSettings?: DeferredRevenueSettings | null;
  contractLengthMonths?: number | null;
  discountTiers?: DiscountTier[];
  /**
   * What the plan promises to deliver. Authored on the plan's own visits — the
   * `coveredServices` array below is the model these replaced, still carried on
   * memberships sold under it and no longer written by anything.
   */
  recurringServices?: Array<{
    id: string;
    label?: string | null;
    jobTypeId?: string | null;
    recurrenceType?: string | null;
    recurrenceInterval?: number | null;
    recurrenceMonths?: number[] | null;
    attachTo?: 'PROPERTY' | 'UNIT' | null;
    coverageId?: string | null;
    firstVisitComplete?: boolean | null;
    durationType?: string | null;
    totalVisits?: number | null;
  }>;
  coveredServices?: Array<{
    lineItemId?: string;
    name: string;
    quantityPerPeriod: number;
    entitlementPeriodType: CoveredServicePeriod;
    rolloverPolicy?: RolloverPolicy;
    rolloverMax?: number | null;
    noShowPolicy?: NoShowPolicy;
    reschedulePolicy?: ReschedulePolicy;
    seasonalWindow?: { startMonth: number; endMonth: number } | null;
    equipmentCategory?: string | null;
    jobTypeId?: string | null;
    firstOfferOffsetMonths?: number | null;
    kind?: CoveredServiceKind;
  }>;
  waivers?: FeeWaiver[];
  renewalRules?: {
    autoRenewDefault: boolean;
    renewalReminderDays: number;
    priceUpliftPercent?: number;
  } | null;
  cancellationRules?: {
    allowSelfService: boolean;
    defaultMode: CancelMode;
    etfEnabled: boolean;
    etfType?: EtfType;
    etfValue?: number;
  } | null;
  saveOfferTemplates?: SaveOfferTemplate[];
  display?: { valuePoints: string[] } | null;
}

export type DeferredRevenueCalculationMode = 'MANUAL' | 'AUTOMATIC';
export type DeferredRevenueAllocationMode = 'EVEN' | 'CUSTOM';
export type DismissedEventRevenuePolicy = 'ALWAYS_RECOGNIZE' | 'PROMPT';
export type CancellationDeferredBalancePolicy =
  | 'ALWAYS_CREATE_INVOICE'
  | 'OPTIONAL_INVOICE'
  | 'DO_NOT_CREATE_INVOICE';

export interface DeferredRevenueSettings {
  calculationMode: DeferredRevenueCalculationMode;
  allocationMode: DeferredRevenueAllocationMode;
  serviceAllocations: Array<{
    recurringServiceId: string;
    percentage?: number | null;
    amountPerEvent?: number | null;
  }>;
  dismissedEventPolicy: DismissedEventRevenuePolicy;
  cancellationBalancePolicy: CancellationDeferredBalancePolicy;
}

export interface MembershipPlanListResponse {
  items: MembershipPlan[];
  total: number;
  page: number;
  limit: number;
}

export interface MembershipPlanForm {
  name: string;
  description: string;
  discountPercent: number;
  priceAmount: number;
  priceCurrency: string;
  billingIntervalValue: number;
  billingIntervalUnit: BillingIntervalUnit;
  eligibleLineItemTypes: string[];
  eligibleLineItemIds: string[];
  excludedLineItemIds: string[];
  priority: number;
  setupFee: number;
  contractLengthMonths: number | null;
  billingOptions: MembershipBillingOption[];
  coverageRules: MembershipCoverageRuleDraft[];
  loadedLaborRate: number;
  costUtilizationPercent: number;
  revenueRecognitionMode: 'INHERIT' | 'AT_SALE' | 'STRAIGHT_LINE' | 'PER_VISIT' | 'VISIT_LINKED';
  visitRecognitionPercent: number;
  deferredRevenueSettings: DeferredRevenueSettings;
  unsupportedLegacyBilling: {
    interval: { value: number; unit: BillingIntervalUnit } | null;
    contractLengthMonths: number | null;
  } | null;
  discountTiers: DiscountTier[];
  coveredServices: CoveredService[];
  feeWaivers: FeeWaiver[];
  autoRenewDefault: boolean;
  renewalReminderDays: number;
  priceUpliftPercent: number;
  allowSelfServiceCancel: boolean;
  defaultCancelMode: CancelMode;
  etfEnabled: boolean;
  etfType: EtfType;
  etfValue: number;
  saveOfferTemplates: SaveOfferTemplate[];
  valuePoints: string[];
}

export interface CreateMembershipPlanPayload {
  name: string;
  description?: string;
  discountPercent: number;
  priceAmount: number;
  priceCurrency?: string;
  billingIntervalValue: number;
  billingIntervalUnit: BillingIntervalUnit;
  eligibleLineItemTypes?: string[];
  eligibleLineItemIds?: string[];
  excludedLineItemIds?: string[];
  priority?: number;
  setupFee?: number;
  contractLengthMonths?: number | null;
  billingOptions?: MembershipBillingOption[];
  coverageRules?: MembershipCoverageRule[];
  recurringServices?: PlanRecurringServiceInput[];
  loadedLaborRate?: number;
  costUtilizationPercent?: number;
  revenueRecognitionMode?: 'AT_SALE' | 'STRAIGHT_LINE' | 'PER_VISIT' | 'VISIT_LINKED' | null;
  visitRecognitionPercent?: number | null;
  deferredRevenueSettings?: DeferredRevenueSettings | null;
  discountTiers?: DiscountTier[];
  coveredServices?: Array<{
    lineItemId?: string;
    name: string;
    quantityPerPeriod: number;
    entitlementPeriodType: CoveredServicePeriod;
    rolloverPolicy: RolloverPolicy;
    rolloverMax?: number | null;
    noShowPolicy: NoShowPolicy;
    reschedulePolicy: ReschedulePolicy;
    seasonalWindow?: { startMonth: number; endMonth: number } | null;
    equipmentCategory?: string | null;
    jobTypeId?: string | null;
    firstOfferOffsetMonths?: number | null;
    kind?: CoveredServiceKind;
  }>;
  waivers?: FeeWaiver[];
  renewalRules?: {
    autoRenewDefault: boolean;
    renewalReminderDays: number;
    priceUpliftPercent?: number;
  };
  cancellationRules?: {
    allowSelfService: boolean;
    defaultMode: CancelMode;
    etfEnabled: boolean;
    etfType?: EtfType;
    etfValue?: number;
  };
  saveOfferTemplates?: SaveOfferTemplate[];
  display?: { valuePoints: string[] };
}

// Canonical UPPER to match pricebook line-item `type` ("SERVICE"). Display is
// case-folded at render. Persisting lowercase here broke the type-eligibility
// match in billing (hallazgo #13).
// FEE is here because a plan discounts fees as readily as it waives them: a
// waiver is all-or-nothing, so "half off the dispatch fee" can only be said as
// an eligibility type or a tier. Both lists stay in step with the pricebook's
// LineItemType; BUNDLE is still absent, which is a separate gap.
export const ITEM_TYPES = ['SERVICE', 'MATERIAL', 'EQUIPMENT', 'FEE'];
export const TIER_APPLIES_TO = ['SERVICE', 'MATERIAL', 'EQUIPMENT', 'FEE'];

export const BILLING_INTERVAL_UNIT_LABELS: Record<BillingIntervalUnit, string> = {
  MONTHS: 'Month(s)',
  YEARS: 'Year(s)',
};

export const BILLING_FREQUENCY_LABELS: Record<BillingFrequency, string> = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  SEMI_ANNUAL: 'Semi-Annual',
  ANNUAL: 'Annual',
};

// Period noun for "per <period>" price captions (e.g. "$299.00 per year"),
// instead of the adjective label ("per annual").
export const BILLING_FREQUENCY_PERIOD: Record<BillingFrequency, string> = {
  MONTHLY: 'month',
  QUARTERLY: 'quarter',
  SEMI_ANNUAL: '6 months',
  ANNUAL: 'year',
};

export const BILLING_FREQUENCY_SHORT: Record<BillingFrequency, string> = {
  MONTHLY: '/mo',
  QUARTERLY: '/qtr',
  SEMI_ANNUAL: '/6mo',
  ANNUAL: '/yr',
};

export const MEMBERSHIP_BILLING_OPTION_FREQUENCY_LABELS: Record<
  MembershipBillingOptionFrequency,
  string
> = {
  MONTHLY: 'Monthly',
  BIMONTHLY: 'Every 2 months',
  QUARTERLY: 'Quarterly',
  SEMIANNUAL: 'Semiannual',
  ANNUAL: 'Annual',
  UPFRONT: 'Upfront',
};

const RECURRING_PERIODS_PER_YEAR: Record<
  Exclude<MembershipBillingOptionFrequency, 'UPFRONT'>,
  number
> = {
  MONTHLY: 12,
  BIMONTHLY: 6,
  QUARTERLY: 4,
  SEMIANNUAL: 2,
  ANNUAL: 1,
};

function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function annualizedBillingOptionRevenue(option: MembershipBillingOption): number {
  if (option.frequency === 'UPFRONT') {
    if (option.termMonths <= 0) return 0;
    return roundMoney(option.basePrice * (12 / option.termMonths));
  }
  return roundMoney(option.basePrice * RECURRING_PERIODS_PER_YEAR[option.frequency]);
}

export function convertBillingOption(
  option: MembershipBillingOption,
  frequency: MembershipBillingOptionFrequency,
  termMonths: MembershipBillingTermMonths = option.termMonths,
  annualRevenueOverride?: number,
): MembershipBillingOption {
  if (frequency === 'UPFRONT' && termMonths === 0) {
    throw new Error('Upfront billing requires a fixed term');
  }
  const annualRevenue = annualRevenueOverride ?? annualizedBillingOptionRevenue(option);
  const basePrice =
    frequency === 'UPFRONT'
      ? annualRevenue * (termMonths / 12)
      : annualRevenue / RECURRING_PERIODS_PER_YEAR[frequency];
  return { ...option, frequency, termMonths, basePrice: roundMoney(basePrice) };
}

export function legacyBillingOption(plan: {
  priceAmount: number;
  billingIntervalValue: number;
  billingIntervalUnit: BillingIntervalUnit;
  contractLengthMonths?: number | null;
}): MembershipBillingOption {
  const intervalValue = toFiniteNumber(plan.billingIntervalValue, 1);
  const frequency: MembershipBillingOptionFrequency =
    plan.billingIntervalUnit === 'YEARS'
      ? 'ANNUAL'
      : intervalValue === 2
        ? 'BIMONTHLY'
        : intervalValue === 3
          ? 'QUARTERLY'
          : intervalValue === 6
            ? 'SEMIANNUAL'
            : 'MONTHLY';
  return {
    id: 'legacy-default',
    label: MEMBERSHIP_BILLING_OPTION_FREQUENCY_LABELS[frequency],
    termMonths: isMembershipBillingFixedTerm(Number(plan.contractLengthMonths))
      ? (Number(plan.contractLengthMonths) as MembershipBillingTermMonths)
      : 0,
    frequency,
    basePrice: toFiniteNumber(plan.priceAmount),
    isDefault: true,
  };
}

export function billingIntervalForOption(option: MembershipBillingOption): {
  billingIntervalValue: number;
  billingIntervalUnit: BillingIntervalUnit;
} {
  switch (option.frequency) {
    case 'BIMONTHLY':
      return { billingIntervalValue: 2, billingIntervalUnit: 'MONTHS' };
    case 'QUARTERLY':
      return { billingIntervalValue: 3, billingIntervalUnit: 'MONTHS' };
    case 'SEMIANNUAL':
      return { billingIntervalValue: 6, billingIntervalUnit: 'MONTHS' };
    case 'ANNUAL':
      return { billingIntervalValue: 1, billingIntervalUnit: 'YEARS' };
    case 'UPFRONT':
      return {
        billingIntervalValue: option.termMonths,
        billingIntervalUnit: 'MONTHS',
      };
    case 'MONTHLY':
    default:
      return { billingIntervalValue: 1, billingIntervalUnit: 'MONTHS' };
  }
}

export function resolveEnrollmentPlanDefaults(plan: MembershipPlan) {
  const options =
    Array.isArray(plan.billingOptions) && plan.billingOptions.length > 0 ? plan.billingOptions : [];
  const option = options.find((candidate) => candidate.isDefault) ?? options[0];
  const interval = option
    ? billingIntervalForOption(option)
    : {
        billingIntervalValue: Number(plan.billingIntervalValue) || 1,
        billingIntervalUnit:
          plan.billingIntervalUnit === 'YEARS' ? ('YEARS' as const) : ('MONTHS' as const),
      };
  const contractMonths = Number(option?.termMonths ?? plan.contractLengthMonths) || 0;

  return {
    billingOptionId: option?.id ?? '',
    collectionMethod:
      option?.frequency === 'UPFRONT' ? ('PREPAID' as const) : ('MANUAL_INVOICE' as const),
    billingAmount: Number.isFinite(Number(option?.basePrice ?? plan.priceAmount))
      ? Number(option?.basePrice ?? plan.priceAmount)
      : 0,
    billingIntervalUnit: interval.billingIntervalUnit,
    billingIntervalValue: interval.billingIntervalValue,
    contractMonths,
  };
}

function toFiniteNumber(value: unknown, fallback = 0): number {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}

function toArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

function isBillingIntervalUnit(value: unknown): value is BillingIntervalUnit {
  return value === 'MONTHS' || value === 'YEARS';
}

export function billingFrequencyFromInterval(
  value: number,
  unit: BillingIntervalUnit,
): BillingFrequency {
  if (unit === 'YEARS') return 'ANNUAL';
  if (value === 3) return 'QUARTERLY';
  if (value === 6) return 'SEMI_ANNUAL';
  return 'MONTHLY';
}

export function billingIntervalFromFrequency(freq: BillingFrequency): {
  billingIntervalValue: number;
  billingIntervalUnit: BillingIntervalUnit;
} {
  if (freq === 'ANNUAL') return { billingIntervalValue: 1, billingIntervalUnit: 'YEARS' };
  if (freq === 'QUARTERLY') return { billingIntervalValue: 3, billingIntervalUnit: 'MONTHS' };
  if (freq === 'SEMI_ANNUAL') return { billingIntervalValue: 6, billingIntervalUnit: 'MONTHS' };
  return { billingIntervalValue: 1, billingIntervalUnit: 'MONTHS' };
}

export function formatBillingInterval(
  value: number | null | undefined,
  unit: BillingIntervalUnit | string | null | undefined,
): string {
  const intervalValue = toFiniteNumber(value);
  if (intervalValue <= 0 || !isBillingIntervalUnit(unit)) return '—';

  if (unit === 'MONTHS') {
    if (intervalValue === 1) return 'Monthly';
    if (intervalValue === 3) return 'Quarterly';
    if (intervalValue === 6) return 'Semi-Annual';
    return `Every ${intervalValue} months`;
  }
  return intervalValue === 1 ? 'Annual' : `Every ${intervalValue} years`;
}

export function formatBillingIntervalShort(
  value: number | null | undefined,
  unit: BillingIntervalUnit | string | null | undefined,
): string {
  const intervalValue = toFiniteNumber(value);
  if (intervalValue <= 0 || !isBillingIntervalUnit(unit)) return '';

  if (unit === 'MONTHS') {
    if (intervalValue === 1) return '/mo';
    if (intervalValue === 3) return '/qtr';
    if (intervalValue === 6) return '/6mo';
    return `/${intervalValue}mo`;
  }
  return intervalValue === 1 ? '/yr' : `/${intervalValue}yr`;
}

export const COVERED_SERVICE_PERIOD_LABELS: Record<CoveredServicePeriod, string> = {
  BILLING_CYCLE: 'Billing Cycle',
  ANNUAL: 'Annual',
  CONTRACT_TERM: 'Contract Term',
  LIFETIME: 'Lifetime',
};

export const WAIVER_TYPE_LABELS: Record<WaiverType, string> = {
  FULL_WAIVE: 'Full Waive',
  DISCOUNT_TO_ZERO: 'Discount to Zero',
  FIXED_CREDIT: 'Fixed Credit',
};

export const EMPTY_FORM: MembershipPlanForm = {
  name: '',
  description: '',
  discountPercent: 10,
  priceAmount: 0,
  priceCurrency: 'USD',
  billingIntervalValue: 1,
  billingIntervalUnit: 'MONTHS',
  eligibleLineItemTypes: [],
  eligibleLineItemIds: [],
  excludedLineItemIds: [],
  priority: 0,
  setupFee: 0,
  contractLengthMonths: null,
  billingOptions: [
    {
      id: 'default-monthly',
      label: 'Monthly',
      termMonths: 0,
      frequency: 'MONTHLY',
      basePrice: 0,
      isDefault: true,
    },
  ],
  coverageRules: [],
  loadedLaborRate: 0,
  costUtilizationPercent: 100,
  revenueRecognitionMode: 'AT_SALE',
  visitRecognitionPercent: 60,
  deferredRevenueSettings: {
    calculationMode: 'AUTOMATIC',
    allocationMode: 'EVEN',
    serviceAllocations: [],
    dismissedEventPolicy: 'PROMPT',
    cancellationBalancePolicy: 'OPTIONAL_INVOICE',
  },
  unsupportedLegacyBilling: null,
  discountTiers: [],
  coveredServices: [],
  feeWaivers: [],
  autoRenewDefault: true,
  renewalReminderDays: 30,
  priceUpliftPercent: 0,
  allowSelfServiceCancel: false,
  defaultCancelMode: 'AT_PERIOD_END',
  etfEnabled: false,
  etfType: 'FLAT',
  etfValue: 0,
  saveOfferTemplates: [],
  valuePoints: [],
};

export function planId(plan: MembershipPlan): string {
  return plan.id ?? '';
}

/**
 * NOT-753 — names of the plan's legacy covered services that promise a visit
 * (anything but a perk or a discount). Only recurring services schedule
 * visits, so on a plan without one these are promises nothing delivers.
 */
export function legacyVisitPromises(
  coveredServices: ReadonlyArray<{ name: string; kind?: CoveredServiceKind }> | undefined,
): string[] {
  return (coveredServices ?? [])
    .filter((service) => (service.kind ?? 'VISIT') === 'VISIT')
    .map((service) => service.name.trim() || 'Included visit');
}

export function formFromPlan(plan: MembershipPlan): MembershipPlanForm {
  const hasAuthoredBillingOptions =
    Array.isArray(plan.billingOptions) && plan.billingOptions.length > 0;
  const billingOptions = hasAuthoredBillingOptions
    ? (plan.billingOptions as MembershipBillingOption[])
    : [legacyBillingOption(plan)];
  const defaultBillingOption =
    billingOptions.find((option) => option.isDefault) ?? billingOptions[0];
  const legacyIntervalValue = toFiniteNumber(plan.billingIntervalValue, 1);
  const legacyIntervalSupported =
    (plan.billingIntervalUnit === 'MONTHS' && [1, 2, 3, 6].includes(legacyIntervalValue)) ||
    (plan.billingIntervalUnit === 'YEARS' && legacyIntervalValue === 1);
  const legacyContractLengthMonths = Number(plan.contractLengthMonths ?? 0);
  const legacyContractTermSupported =
    legacyContractLengthMonths === 0 || isMembershipBillingFixedTerm(legacyContractLengthMonths);
  const unsupportedLegacyBilling =
    !hasAuthoredBillingOptions && (!legacyIntervalSupported || !legacyContractTermSupported)
      ? {
          interval: legacyIntervalSupported
            ? null
            : {
                value: legacyIntervalValue,
                unit: plan.billingIntervalUnit,
              },
          contractLengthMonths: legacyContractTermSupported ? null : legacyContractLengthMonths,
        }
      : null;
  const defaultInterval = unsupportedLegacyBilling?.interval
    ? {
        billingIntervalValue: unsupportedLegacyBilling.interval.value,
        billingIntervalUnit: unsupportedLegacyBilling.interval.unit,
      }
    : billingIntervalForOption(defaultBillingOption);
  return {
    name: plan.name,
    description: plan.description ?? '',
    discountPercent: toFiniteNumber(plan.discountPercent),
    priceAmount: toFiniteNumber(defaultBillingOption.basePrice),
    priceCurrency: plan.priceCurrency ?? 'USD',
    billingIntervalValue: defaultInterval.billingIntervalValue,
    billingIntervalUnit: defaultInterval.billingIntervalUnit,
    eligibleLineItemTypes: toArray(plan.eligibleLineItemTypes).map((type) => type.toUpperCase()),
    eligibleLineItemIds: toArray(plan.eligibleLineItemIds),
    excludedLineItemIds: toArray(plan.excludedLineItemIds),
    priority: toFiniteNumber(plan.priority),
    setupFee: plan.setupFee ?? 0,
    contractLengthMonths:
      unsupportedLegacyBilling?.contractLengthMonths ?? (defaultBillingOption.termMonths || null),
    billingOptions,
    // NOT-628 §3 — "the first coverage row is the primary type". It is a
    // consequence of order, so a stored row that disagrees is normalized on read
    // instead of leaving the form invalid with no control to fix it.
    coverageRules: toArray(plan.coverageRules).map((rule, index) => ({
      id: rule.id,
      equipmentCategory: rule.equipmentCategory,
      includedQuantity: toFiniteNumber(rule.includedQuantity),
      ...extraUnitPriceOf(rule),
      isPrimary: index === 0,
    })),
    loadedLaborRate: toFiniteNumber(plan.loadedLaborRate),
    costUtilizationPercent: toFiniteNumber(plan.costUtilizationPercent, 100),
    revenueRecognitionMode: plan.revenueRecognitionMode ?? 'INHERIT',
    visitRecognitionPercent: toFiniteNumber(plan.visitRecognitionPercent, 60),
    deferredRevenueSettings: {
      calculationMode: plan.deferredRevenueSettings?.calculationMode ?? 'AUTOMATIC',
      allocationMode: plan.deferredRevenueSettings?.allocationMode ?? 'EVEN',
      serviceAllocations: toArray(plan.deferredRevenueSettings?.serviceAllocations).map(
        (allocation) => ({
          recurringServiceId: allocation.recurringServiceId,
          percentage: allocation.percentage == null ? null : toFiniteNumber(allocation.percentage),
          amountPerEvent:
            allocation.amountPerEvent == null ? null : toFiniteNumber(allocation.amountPerEvent),
        }),
      ),
      dismissedEventPolicy: plan.deferredRevenueSettings?.dismissedEventPolicy ?? 'PROMPT',
      cancellationBalancePolicy:
        plan.deferredRevenueSettings?.cancellationBalancePolicy ?? 'OPTIONAL_INVOICE',
    },
    unsupportedLegacyBilling,
    discountTiers: toArray(plan.discountTiers).map((tier) => ({
      label: tier.label ?? '',
      appliesTo: toArray(tier.appliesTo),
      specificItemIds: toArray((tier as { specificItemIds?: string[] }).specificItemIds),
      type: tier.type ?? 'PERCENT',
      value: toFiniteNumber(tier.value),
    })),
    coveredServices: (plan.coveredServices ?? []).map((s) => ({
      lineItemId: s.lineItemId,
      name: s.name,
      quantityPerPeriod: s.quantityPerPeriod,
      period: s.entitlementPeriodType,
      rolloverPolicy: s.rolloverPolicy ?? 'EXPIRE',
      rolloverMax: s.rolloverMax ?? null,
      noShowPolicy: s.noShowPolicy ?? 'PRESERVE',
      reschedulePolicy: s.reschedulePolicy ?? 'PRESERVE',
      seasonalWindow: s.seasonalWindow ?? null,
      equipmentCategory: s.equipmentCategory ?? null,
      jobTypeId: s.jobTypeId ?? null,
      firstOfferOffsetMonths: s.firstOfferOffsetMonths ?? null,
      kind: s.kind ?? undefined,
    })),
    feeWaivers: plan.waivers ?? [],
    autoRenewDefault: plan.renewalRules?.autoRenewDefault ?? true,
    renewalReminderDays: plan.renewalRules?.renewalReminderDays ?? 30,
    priceUpliftPercent: plan.renewalRules?.priceUpliftPercent ?? 0,
    allowSelfServiceCancel: plan.cancellationRules?.allowSelfService ?? false,
    defaultCancelMode: plan.cancellationRules?.defaultMode ?? 'AT_PERIOD_END',
    etfEnabled: plan.cancellationRules?.etfEnabled ?? false,
    etfType: plan.cancellationRules?.etfType ?? 'FLAT',
    etfValue: plan.cancellationRules?.etfValue ?? 0,
    saveOfferTemplates: toArray(plan.saveOfferTemplates).map((offer) => ({
      type: offer.type,
      value: offer.value,
      label: offer.label ?? '',
    })),
    valuePoints: plan.display?.valuePoints ?? [],
  };
}

export function formToDto(form: MembershipPlanForm): CreateMembershipPlanPayload {
  const defaultBillingOption =
    form.billingOptions.find((option) => option.isDefault) ?? form.billingOptions[0];
  const legacyInterval = form.unsupportedLegacyBilling?.interval
    ? {
        billingIntervalValue: form.unsupportedLegacyBilling.interval.value,
        billingIntervalUnit: form.unsupportedLegacyBilling.interval.unit,
      }
    : billingIntervalForOption(defaultBillingOption);
  return {
    name: form.name,
    description: form.description || undefined,
    discountPercent: form.discountPercent,
    priceAmount: defaultBillingOption.basePrice,
    priceCurrency: form.priceCurrency || undefined,
    billingIntervalValue: legacyInterval.billingIntervalValue,
    billingIntervalUnit: legacyInterval.billingIntervalUnit,
    eligibleLineItemTypes: form.eligibleLineItemTypes,
    eligibleLineItemIds: form.eligibleLineItemIds.length ? form.eligibleLineItemIds : undefined,
    excludedLineItemIds: form.excludedLineItemIds.length ? form.excludedLineItemIds : undefined,
    priority: form.priority,
    setupFee: form.setupFee,
    contractLengthMonths:
      form.unsupportedLegacyBilling?.contractLengthMonths ??
      (defaultBillingOption.termMonths || null),
    billingOptions: form.unsupportedLegacyBilling ? undefined : form.billingOptions,
    coverageRules: form.coverageRules.map((rule, index) => ({
      id: rule.id,
      equipmentCategory: rule.equipmentCategory,
      includedQuantity: rule.includedQuantity,
      extraUnitPrice: rule.extraUnitPrice,
      extraUnitPricePer: rule.extraUnitPricePer,
      isPrimary: index === 0,
    })),
    loadedLaborRate: form.loadedLaborRate,
    costUtilizationPercent: form.costUtilizationPercent,
    revenueRecognitionMode:
      form.revenueRecognitionMode === 'INHERIT' ? null : form.revenueRecognitionMode,
    visitRecognitionPercent:
      form.revenueRecognitionMode === 'VISIT_LINKED' ? form.visitRecognitionPercent : null,
    deferredRevenueSettings:
      form.revenueRecognitionMode === 'PER_VISIT' ? form.deferredRevenueSettings : null,
    discountTiers: form.discountTiers.length ? form.discountTiers : undefined,
    coveredServices: form.coveredServices.length
      ? form.coveredServices.map((s) => ({
          lineItemId: s.lineItemId,
          name: s.name,
          quantityPerPeriod: s.quantityPerPeriod,
          entitlementPeriodType: s.period,
          rolloverPolicy: s.rolloverPolicy,
          rolloverMax: s.rolloverPolicy === 'ROLLOVER_LIMITED' ? (s.rolloverMax ?? null) : null,
          noShowPolicy: s.noShowPolicy,
          reschedulePolicy: s.reschedulePolicy,
          seasonalWindow: s.seasonalWindow ?? undefined,
          equipmentCategory: s.equipmentCategory ?? undefined,
          jobTypeId: s.jobTypeId ?? undefined,
          firstOfferOffsetMonths: s.firstOfferOffsetMonths ?? undefined,
          kind: s.kind ?? undefined,
        }))
      : undefined,
    waivers: form.feeWaivers.length ? form.feeWaivers : undefined,
    renewalRules: {
      autoRenewDefault: form.autoRenewDefault,
      renewalReminderDays: form.renewalReminderDays,
      priceUpliftPercent: form.priceUpliftPercent || 0,
    },
    cancellationRules: {
      allowSelfService: form.allowSelfServiceCancel,
      defaultMode: form.defaultCancelMode,
      etfEnabled: form.etfEnabled,
      etfType: form.etfEnabled ? form.etfType : undefined,
      // NOT-254: a remaining-contract % can't exceed 100. Clamp at submit so a
      // legacy plan loaded with a stale >100 value and saved untouched (no
      // field interaction) still persists a valid percentage.
      etfValue: form.etfEnabled
        ? form.etfType === 'PERCENT_REMAINING'
          ? Math.min(form.etfValue, 100)
          : form.etfValue
        : undefined,
    },
    saveOfferTemplates: normalizeMembershipSaveOffers(form.saveOfferTemplates, form.priceCurrency),
    display: form.valuePoints.length ? { valuePoints: form.valuePoints } : undefined,
  };
}

export function duplicateMembershipBillingOptions(
  options: MembershipBillingOption[] | undefined,
): MembershipBillingOption[] | undefined {
  if (!options?.length) return undefined;
  return options.map((option) => ({
    ...option,
    id: createUuid(),
  }));
}

export function normalizeMembershipSaveOffers(
  offers: SaveOfferTemplate[],
  currency = 'USD',
): SaveOfferTemplate[] {
  return offers.flatMap((offer) => {
    if (offer.type !== 'CREDIT') {
      return [{ ...offer, label: offer.label.trim() }];
    }

    const value = normalizeMembershipCreditAmount(offer.value);
    if (value == null) return [];

    return {
      ...offer,
      value,
      label: `${formatCurrency(value, currency)} account credit`,
    };
  });
}

export function formatDiscountPercent(value: number): string {
  return `${value}%`;
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
}

export function formatDiscount(
  type: DiscountType | string | null | undefined,
  value: number | null | undefined,
): string {
  const amount = toFiniteNumber(value);
  if (type === 'FLAT') return formatCurrency(amount);
  if (type === 'SET_PRICE') return `Set price ${formatCurrency(amount)}`;
  return `${amount}%`;
}

export function formatMembershipPlanDiscount(plan: MembershipPlan): string {
  // NOT-257: derive from the tier-aware headline percent so a custom-rate plan
  // (discountPercent stored 0 + a 20% tier) doesn't render "0% off" in callers
  // that format this directly (e.g. the plan detail page). Callers that want a
  // "Custom rates" label gate on membershipPlanHasCustomRates() first.
  return formatDiscount('PERCENT', membershipPlanDiscountPercent(plan));
}

export function membershipPlanHasCustomRates(plan: MembershipPlan): boolean {
  return (plan.discountTiers ?? []).length > 0;
}

export function formatMembershipPlanCustomRatesSummary(plan: MembershipPlan): string {
  const count = (plan.discountTiers ?? []).length;
  return `${count} custom rate${count === 1 ? '' : 's'}`;
}

export function membershipPlanDiscountPercent(plan: MembershipPlan): number {
  // NOT-257: a plan that bills by custom rate tiers stores discountPercent = 0
  // (single source of truth), so surface the highest PERCENT tier instead —
  // otherwise tiered plans drop out of "discount > 0" filters and sort into the
  // zero bucket. Simple plans fall back to discountPercent. (Flat / set-price
  // tiers have no percentage and contribute 0.)
  const tiers = toArray(plan.discountTiers);
  if (tiers.length > 0) {
    const percents = tiers
      .filter((tier) => tier.type === 'PERCENT')
      .map((tier) => toFiniteNumber(tier.value));
    return percents.length > 0 ? Math.max(...percents) : 0;
  }
  return toFiniteNumber(plan.discountPercent);
}

export function hasMembershipPlanDiscount(plan: MembershipPlan): boolean {
  return membershipPlanDiscountPercent(plan) > 0;
}

export function membershipPlanBillingAmount(plan: MembershipPlan): number {
  return toFiniteNumber(plan.priceAmount);
}

export function membershipPlanApplicableTypes(plan: MembershipPlan): string[] {
  return plan.eligibleLineItemTypes ?? [];
}

export function membershipPlanBillingFrequency(plan: MembershipPlan): BillingFrequency {
  return billingFrequencyFromInterval(plan.billingIntervalValue, plan.billingIntervalUnit);
}
